import { push } from 'connected-react-router';
import { catchError, concatAll, map, mergeMap, filter } from 'rxjs/operators';
import { from, of } from 'rxjs';
import i18next from 'i18next';
import RootEpic from 'store/RootEpic';
import Helper from 'utils/Helper';
import { showNotification, showNotificationFromError } from 'components/app/store/UserSlice';
import { CONSOLE_ROUTE, EOS_SERVICE_ROUTE } from 'Route';
import { PermissionAction, PermissionResource, hasPermission } from 'gql/Permission';
import { createEosService, deleteEosService, getEosService, listEosServices, updateEosService } from 'gql/EosService';
import EosServiceSlice from './EosServiceSlice';

// LIST
export const listEosServicesEpic: RootEpic = (action$, state$) => {
  return action$.pipe(
    filter(() =>
      hasPermission(state$.value.user.user?.permissions, PermissionAction.READ, PermissionResource.EOS_SERVICE)
    ),
    filter(EosServiceSlice.actions.listEosServices.match),
    mergeMap(({ payload }) => {
      return from(listEosServices(payload)).pipe(
        map((response) => {
          return response.nextToken
            ? [
                EosServiceSlice.actions.listEosServicesSucceeded(response.items),
                EosServiceSlice.actions.listEosServices({
                  nextToken: response.nextToken,
                  size: payload?.size,
                  name: payload?.name,
                  clusterInstanceName: payload?.clusterInstanceName,
                  namespace: payload?.namespace,
                }),
              ]
            : [EosServiceSlice.actions.listEosServicesSucceeded(response.items)];
        }),
        concatAll(),
        catchError((error) => {
          return of(EosServiceSlice.actions.listEosServicesFailed(error));
        })
      );
    })
  );
};

export const listEosServicesFailedEpic: RootEpic = (action$) =>
  action$.pipe(
    filter(EosServiceSlice.actions.listEosServicesFailed.match),
    map(({ payload }) => showNotificationFromError({ msg: i18next.t('FAILED_GET_EOS_SERVICES'), err: payload }))
  );

// GET
export const getEosServiceEpic: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(() =>
      hasPermission(state$.value.user.user?.permissions, PermissionAction.READ, PermissionResource.EOS_SERVICE)
    ),
    filter(EosServiceSlice.actions.getEosService.match),
    mergeMap(({ payload }) => {
      return from(getEosService(payload)).pipe(
        map((response) => {
          return EosServiceSlice.actions.getEosServiceSucceeded(response);
        }),
        catchError((error) => {
          return of(EosServiceSlice.actions.getEosServiceFailed(error));
        })
      );
    })
  );

// CREATE
export const createEosServiceEpic: RootEpic = (action$) =>
  action$.pipe(
    filter(EosServiceSlice.actions.createEosService.match),
    mergeMap(({ payload }) =>
      from(createEosService(payload)).pipe(
        map((response) => EosServiceSlice.actions.createEosServiceSucceeded(response)),
        catchError((error) => of(EosServiceSlice.actions.createEosServiceFailed(error)))
      )
    )
  );

export const createEosServiceSuccessEpic: RootEpic = (action$) =>
  action$.pipe(
    filter(EosServiceSlice.actions.createEosServiceSucceeded.match),
    mergeMap((action) => {
      return [
        showNotification({ type: 'success', contents: i18next.t('SUCCESS_CREATED_EOS_SERVICE', action.payload.id) }),
        push(Helper.interpolateRoute(EOS_SERVICE_ROUTE.url, { eosServiceId: action.payload.id })),
      ];
    })
  );

export const createEosServiceFailedEpic: RootEpic = (action$) =>
  action$.pipe(
    filter(EosServiceSlice.actions.createEosServiceFailed.match),
    map(({ payload }) => showNotificationFromError({ msg: i18next.t('FAILED_CREATE_EOS_SERVICE'), err: payload }))
  );

// UPDATE
export const updateEosServiceEpic: RootEpic = (action$) =>
  action$.pipe(
    filter(EosServiceSlice.actions.updateEosService.match),
    mergeMap(({ payload }) => {
      return from(updateEosService(payload)).pipe(
        map((response) => EosServiceSlice.actions.updateEosServiceSucceeded(response)),
        catchError((error) => of(EosServiceSlice.actions.updateEosServiceFailed(error)))
      );
    })
  );

export const updateEosServiceSuccessEpic: RootEpic = (action$) =>
  action$.pipe(
    filter(EosServiceSlice.actions.updateEosServiceSucceeded.match),
    mergeMap((action) => {
      return [
        showNotification({ type: 'success', contents: i18next.t('SUCCESS_UPDATED_EOS_SERVICE', action.payload.id) }),
        push(Helper.interpolateRoute(EOS_SERVICE_ROUTE.url, { eosServiceId: action.payload.id })),
      ];
    })
  );

export const updateEosServiceFailedEpic: RootEpic = (action$) =>
  action$.pipe(
    filter(EosServiceSlice.actions.updateEosServiceFailed.match),
    map(({ payload }) => {
      return showNotificationFromError({ msg: i18next.t('FAILED_UPDATE_EOS_SERVICE'), err: payload });
    })
  );

// DELETE
export const deleteEosServiceEpic: RootEpic = (action$) =>
  action$.pipe(
    filter(EosServiceSlice.actions.deleteEosService.match),
    mergeMap(({ payload }) => {
      return from(deleteEosService(payload)).pipe(
        map((response) => EosServiceSlice.actions.deleteEosServiceSucceeded(response)),
        catchError((error) => of(EosServiceSlice.actions.deleteEosServiceFailed(error)))
      );
    })
  );

export const deleteEosServiceSuccessEpic: RootEpic = (action$) =>
  action$.pipe(
    filter(EosServiceSlice.actions.deleteEosServiceSucceeded.match),
    mergeMap((action) => {
      return [
        showNotification({ type: 'success', contents: i18next.t('SUCCESS_DELETED_EOS_SERVICE', action.payload.id) }),
        push(`${CONSOLE_ROUTE.url}?tab=eosService`),
      ];
    })
  );

export const deleteEosServiceFailedEpic: RootEpic = (action$) =>
  action$.pipe(
    filter(EosServiceSlice.actions.deleteEosServiceFailed.match),
    map(({ payload }) => {
      return showNotificationFromError({ msg: i18next.t('FAILED_DELETE_EOS_SERVICE'), err: payload });
    })
  );

const EosServiceEpics: RootEpic[] = [
  listEosServicesEpic,
  listEosServicesFailedEpic,
  getEosServiceEpic,
  createEosServiceEpic,
  createEosServiceSuccessEpic,
  createEosServiceFailedEpic,
  updateEosServiceEpic,
  updateEosServiceSuccessEpic,
  updateEosServiceFailedEpic,
  deleteEosServiceEpic,
  deleteEosServiceSuccessEpic,
  deleteEosServiceFailedEpic,
];

export default EosServiceEpics;
